// @ts-nocheck
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import LoadingOverlay from '../components/LoadingOverlay';
import StyledDataGrid from '../components/StyledDataGrid';

interface BusinessContactsProps {
  access?: string;
  isGlobalAdmin?: boolean;
  userContext?: any;
}

const BusinessContacts: React.FC<BusinessContactsProps> = ({
  access = 'none',
  isGlobalAdmin = false,
  userContext
}) => {
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<any>({
    company_name: '',
    contact_name: '',
    contact_type: 'vendor',
    email: '',
    phone_number: '',
    fax_number: '',
    address: '',
    remarks: '',
    is_active: true
  });
  const [filterType, setFilterType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('true');

  // Check if user is admin
  const userRole = userContext?.highest_role;
  const isAdmin = isGlobalAdmin || userRole === 'company_admin' || userRole === 'project_admin';

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/business-contacts?';
      if (filterType) url += `contact_type=${filterType}&`;
      if (filterActive) url += `is_active=${filterActive}`;

      const response = await fetch(apiUrl(url));
      if (!response.ok) throw new Error('Failed to fetch contacts');
      const data = await response.json();
      setContacts(data.business_contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterActive]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleOpenDialog = (contact: any | null = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        company_name: contact.company_name || '',
        contact_name: contact.contact_name || '',
        contact_type: contact.contact_type || 'vendor',
        email: contact.email || '',
        phone_number: contact.phone_number || '',
        fax_number: contact.fax_number || '',
        address: contact.address || '',
        remarks: contact.remarks || '',
        is_active: contact.is_active !== false
      });
    } else {
      setEditingContact(null);
      setFormData({
        company_name: '',
        contact_name: '',
        contact_type: 'vendor',
        email: '',
        phone_number: '',
        fax_number: '',
        address: '',
        remarks: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleSave = async () => {
    if (!formData.company_name) {
      alert('Company name is required');
      return;
    }

    try {
      setLoading(true);

      const url = editingContact
        ? apiUrl(`/api/business-contacts/${editingContact.contact_id}`)
        : apiUrl('/api/business-contacts');

      const method = editingContact ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save contact');
      }

      await fetchContacts();
      handleCloseDialog();
    } catch (error: any) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this contact?')) return;

    try {
      setDeletingId(contactId);
      const response = await fetch(apiUrl(`/api/business-contacts/${contactId}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete contact');

      await fetchContacts();
    } catch (error: any) {
      alert(error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'vendor': return 'primary';
      case 'supplier': return 'secondary';
      case 'client': return 'success';
      case 'contractor': return 'warning';
      case 'consultant': return 'info';
      default: return 'default';
    }
  };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'company_name',
      headerName: 'Company Name',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'contact_name',
      headerName: 'Contact Person',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'contact_type',
      headerName: 'Type',
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => {
        // Return type label for filtering to work
        return row.contact_type || 'vendor';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.contact_type || 'vendor'}
          color={getTypeColor(params.row.contact_type)}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'phone_number',
      headerName: 'Phone',
      flex: 1.2,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      valueGetter: (value, row) => {
        // Return status label for filtering to work
        return row.is_active ? 'Active' : 'Inactive';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'default'}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {isAdmin && (
            <>
              <Tooltip title="Edit Contact">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(params.row)}
                  disabled={!!deletingId}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {params.row.is_active && (
                <Tooltip title="Deactivate Contact">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(params.row.contact_id)}
                    disabled={deletingId === params.row.contact_id}
                    color="error"
                  >
                    {deletingId === params.row.contact_id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      )
    }
  ];

  // Remove actions column if not admin
  const visibleColumns = isAdmin ? columns : columns.filter(col => col.field !== 'actions');

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <LoadingOverlay loading={loading} size={50} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Contact
          </Button>
        )}
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
          You have read-only access to business contacts. Admin permission required to create or edit contacts.
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Filters
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            <TextField
              fullWidth
              select
              label="Contact Type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="supplier">Supplier</MenuItem>
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="contractor">Contractor</MenuItem>
              <MenuItem value="consultant">Consultant</MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              label="Status"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              size="small"
            >
              <MenuItem value="true">Active Only</MenuItem>
              <MenuItem value="false">Inactive Only</MenuItem>
              <MenuItem value="">All</MenuItem>
            </TextField>
          </Box>
        </Paper>
      </Box>

      {/* Business Contacts DataGrid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={contacts}
          columns={visibleColumns}
          getRowId={(row) => row.contact_id}
          loading={loading}
          paginationMode="client"
        />
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingContact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Contact Type"
                value={formData.contact_type}
                onChange={(e) => setFormData({ ...formData, contact_type: e.target.value })}
              >
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="contractor">Contractor</MenuItem>
                <MenuItem value="consultant">Consultant</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fax Number"
                value={formData.fax_number}
                onChange={(e) => setFormData({ ...formData, fax_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editingContact ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessContacts;
